import { toaster } from '@/components/ui/toaster';
import { templateLibraryQueryKey } from '@/hooks/queries/use-template-library-query';
import api from '@/libs/axiosInstance';
import { ErrorCode } from '@fluenceur/types';
import type {
  ApiResponse,
  CreateTemplateLibraryInput,
  TemplateLibraryEntry,
  UpdateTemplateLibraryInput,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import i18next from 'i18next';

interface ApiErrorResponse {
  message: string;
  code?: string;
  data?: { variables?: unknown };
}

const errorDescription = (error: AxiosError<ApiErrorResponse>, fallbackKey: string): string => {
  const body = error.response?.data;
  const variables = Array.isArray(body?.data?.variables)
    ? body.data.variables.filter((variable): variable is string => typeof variable === 'string')
    : [];
  if (variables.length) {
    const options = { variables: variables.map((variable) => `{{${variable}}}`).join(', ') };
    if (body?.code === ErrorCode.E_TEMPLATE_MISSING_REQUIRED_VARIABLE) {
      return i18next.t('templates.form.errors.missingRequiredVariables', options);
    }
    if (body?.code === ErrorCode.E_TEMPLATE_INVALID_VARIABLE) {
      return i18next.t('templates.form.errors.invalidVariables', options);
    }
  }
  return (
    (body?.code ? i18next.t(`errors.${body.code}`, { defaultValue: '' }) : '') ||
    body?.message?.trim() ||
    i18next.t(fallbackKey)
  );
};

const useTemplateLibraryInvalidate = (brandId: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: templateLibraryQueryKey(brandId) });
};

// ===== Create =====

export const useCreateTemplateAction = (brandId: string) => {
  const invalidate = useTemplateLibraryInvalidate(brandId);

  return useMutation<
    TemplateLibraryEntry,
    AxiosError<ApiErrorResponse>,
    CreateTemplateLibraryInput
  >({
    mutationFn: async (input) => {
      const { data } = await api.post<ApiResponse<TemplateLibraryEntry>>(
        `/dashboard/brands/${brandId}/templates`,
        input
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Failed to create template');
      }
      return data.data;
    },
    onSuccess: () => {
      void invalidate();
      toaster.success({ title: i18next.t('templates.toasts.created') });
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'templates.toasts.createError'),
      });
    },
  });
};

// ===== Update =====

interface UpdateTemplateVariables {
  templateId: string;
  input: UpdateTemplateLibraryInput;
}

export const useUpdateTemplateAction = (brandId: string) => {
  const invalidate = useTemplateLibraryInvalidate(brandId);

  return useMutation<TemplateLibraryEntry, AxiosError<ApiErrorResponse>, UpdateTemplateVariables>({
    mutationFn: async ({ templateId, input }) => {
      const { data } = await api.put<ApiResponse<TemplateLibraryEntry>>(
        `/dashboard/brands/${brandId}/templates/${templateId}`,
        input
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Failed to update template');
      }
      return data.data;
    },
    onSuccess: () => {
      void invalidate();
      toaster.success({ title: i18next.t('templates.toasts.updated') });
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'templates.toasts.updateError'),
      });
    },
  });
};

// ===== Delete =====

export const useDeleteTemplateAction = (brandId: string) => {
  const invalidate = useTemplateLibraryInvalidate(brandId);

  return useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (templateId) => {
      await api.delete(`/dashboard/brands/${brandId}/templates/${templateId}`);
    },
    onSuccess: () => {
      void invalidate();
      toaster.success({ title: i18next.t('templates.toasts.deleted') });
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'templates.toasts.deleteError'),
      });
    },
  });
};

// ===== Set as default =====

export const useSetDefaultTemplateAction = (brandId: string) => {
  const invalidate = useTemplateLibraryInvalidate(brandId);

  return useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (templateId) => {
      await api.post(`/dashboard/brands/${brandId}/templates/${templateId}/default`);
    },
    onSuccess: () => {
      void invalidate();
      toaster.success({ title: i18next.t('templates.toasts.defaultSet') });
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'templates.toasts.defaultSetError'),
      });
    },
  });
};
